/**
 * Inspector for the **Validate** node.
 *
 * Two modes, and the second is the point. Typing rules by hand works, but the
 * target already publishes most of them — Vault knows which fields are
 * required, how long they may be, which picklist values exist, which keys are
 * unique. Reading those beats restating them: they are the rules the load will
 * actually be judged by, and a restated rule drifts the first time somebody
 * changes the vault.
 */

import { useEffect, useState } from "react";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Checkbox,
  Group,
  SegmentedControl,
  Select,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";

import { useAccessToken } from "@/features/auth/useAccessToken";
import { listConnections, type ConnectionOut } from "@/shared/api/integration";
import { veevaListObjects, type ObjectSummary } from "@/shared/api/integration-browse";

import { toastApiError } from "@/shared/ui/toast";

import { ConnectionRefSelect } from "./ConnectionRefSelect";
import { useConnectionRef } from "./useConnectionRef";

type RuleRow = { column: string; rule: string; value?: string };

/** The rules, in the order a person reasons about them: is it there, is it
 *  right, is it the only one. */
const RULES: { value: string; label: string; needs?: string }[] = [
  { value: "required", label: "Required — must not be empty" },
  { value: "allowed", label: "One of these values", needs: "active__v, inactive__v" },
  { value: "max_length", label: "No longer than", needs: "128" },
  { value: "pattern", label: "Matches this pattern", needs: "^[A-Z]{2}-\\d+$" },
  { value: "number", label: "Is a number" },
  { value: "date", label: "Is a date (ISO, 2026-09-22)" },
  { value: "unique", label: "Unique within the batch" },
];

const NEEDS_VALUE = new Set(RULES.filter((r) => r.needs).map((r) => r.value));

export function ValidateConfig({
  config,
  editable,
  onChange,
  inputColumns,
}: {
  config: Record<string, unknown>;
  editable: boolean;
  onChange: (key: string, value: unknown) => void;
  inputColumns: Record<string, string[]>;
}) {
  const getToken = useAccessToken();
  const mode = String(config.mode ?? "rules");
  const rules: RuleRow[] = Array.isArray(config.rules)
    ? (config.rules as RuleRow[]).map((r) => ({ ...r }))
    : [];
  const columns = inputColumns["in-0"] ?? [];

  const [connections, setConnections] = useState<ConnectionOut[]>([]);
  const [objects, setObjects] = useState<ObjectSummary[]>([]);
  // The node names a connection id or a `@role`; metadata takes an id.
  const {
    connectionId,
    role,
    settled: roleSettled,
  } = useConnectionRef(String(config.connectionId ?? ""));

  useEffect(() => {
    if (mode !== "veeva") return;
    let cancelled = false;
    void (async () => {
      try {
        const items = (await listConnections(await getToken())).items;
        if (!cancelled) setConnections(items.filter((c) => c.source_type === "veeva"));
      } catch (err) {
        toastApiError("Could not load connections", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, mode]);

  useEffect(() => {
    if (mode !== "veeva" || !connectionId) return;
    let cancelled = false;
    void (async () => {
      try {
        const items = (await veevaListObjects(await getToken(), connectionId)).items;
        if (!cancelled) setObjects(items);
      } catch (err) {
        toastApiError("Could not load the vault's objects", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, mode, connectionId]);

  const setRules = (next: RuleRow[]) => onChange("rules", next);
  const patch = (index: number, change: Partial<RuleRow>) =>
    setRules(rules.map((r, i) => (i === index ? { ...r, ...change } : r)));

  return (
    <Stack gap="sm">
      <SegmentedControl
        value={mode}
        onChange={(v) => onChange("mode", v)}
        disabled={!editable}
        fullWidth
        data={[
          { value: "rules", label: "My rules" },
          { value: "veeva", label: "Veeva's rules" },
        ]}
      />

      {mode === "veeva" && (
        <>
          <ConnectionRefSelect
            label="Connection"
            placeholder="Pick a Veeva connection"
            data={connections.map((c) => ({ value: c.connection_id, label: c.display_name }))}
            connectionId={connectionId}
            role={role}
            settled={roleSettled}
            editable={editable}
            onChange={(v) => onChange("connectionId", v)}
          />
          <Select
            label="Object the rows are going to"
            description="Its field metadata becomes the rules: required fields, lengths, picklist values, unique keys."
            placeholder={connectionId ? "Pick an object" : "Pick a connection first"}
            data={objects.map((o) => ({ value: o.name, label: `${o.label ?? o.name}` }))}
            value={String(config.object ?? "") || null}
            onChange={(v) => onChange("object", v ?? "")}
            disabled={!editable || !connectionId}
            searchable
          />
          <Checkbox
            label="Stop if a column has no matching field"
            description="A column the object does not have would fail every row for the same reason — that is a mapping mistake, not a data problem."
            checked={config.refuseUnknownColumns !== false}
            onChange={(e) => onChange("refuseUnknownColumns", e.currentTarget.checked)}
            disabled={!editable}
          />
          <Checkbox
            label="Stop if a required field has no column"
            description="Off by default, and deliberately: Veeva marks fields required that it fills itself (a product's Status, for one), and its metadata gives no way to tell those apart. Turn this on only when you know every required field really must come from your data."
            checked={Boolean(config.refuseMissingRequired)}
            onChange={(e) => onChange("refuseMissingRequired", e.currentTarget.checked)}
            disabled={!editable}
          />
          <Text size="xs" c="dimmed">
            Any rules below are applied as well — the vault's rules are a floor, not a ceiling.
          </Text>
        </>
      )}

      <Group justify="space-between" align="center">
        <Text size="sm" fw={600}>
          Rules
        </Text>
        <Button
          size="compact-xs"
          variant="light"
          disabled={!editable}
          onClick={() => setRules([...rules, { column: columns[0] ?? "", rule: "required" }])}
        >
          Add rule
        </Button>
      </Group>

      {rules.length === 0 && mode === "rules" && (
        <Alert color="orange" variant="light">
          A Validate node with no rules refuses to run rather than passing everything — that would
          be the worst possible default for a step whose job is to check.
        </Alert>
      )}

      {rules.map((rule, i) => (
        <Stack key={i} gap={4}>
          <Group gap={4} wrap="nowrap" align="flex-end">
            <Select
              label={i === 0 ? "Column" : undefined}
              data={Array.from(new Set([...columns, rule.column].filter(Boolean)))}
              value={rule.column || null}
              onChange={(v) => patch(i, { column: v ?? "" })}
              disabled={!editable}
              searchable
              placeholder={columns.length ? "Column" : "Test the source first"}
              style={{ flex: 1 }}
            />
            <ActionIcon
              variant="subtle"
              color="red"
              disabled={!editable}
              aria-label="Remove rule"
              onClick={() => setRules(rules.filter((_, j) => j !== i))}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
          <Select
            data={RULES}
            value={rule.rule}
            onChange={(v) => patch(i, { rule: v ?? "required" })}
            disabled={!editable}
          />
          {NEEDS_VALUE.has(rule.rule) && (
            <TextInput
              value={rule.value ?? ""}
              onChange={(e) => patch(i, { value: e.currentTarget.value })}
              disabled={!editable}
              placeholder={RULES.find((r) => r.value === rule.rule)?.needs}
            />
          )}
        </Stack>
      ))}

      <Checkbox
        label="Stop the run if any row is invalid"
        description="For a migration that must be all-or-nothing. Off by default: the usual answer is to load the good rows and save the rest as a fix-list."
        checked={Boolean(config.failIfInvalid)}
        onChange={(e) => onChange("failIfInvalid", e.currentTarget.checked)}
        disabled={!editable}
      />

      <Alert color="cyan" variant="light" title="Two outputs">
        {/* `component="div"`: the badges below render as elements a <p> may not
            contain, and React is right to complain about it. */}
        <Text size="sm" component="div">
          <Badge size="xs" color="teal" variant="light">
            valid
          </Badge>{" "}
          carries the rows a load will accept.{" "}
          <Badge size="xs" color="orange" variant="light">
            invalid
          </Badge>{" "}
          carries the rest, each with <b>_row</b> (its position) and <b>_errors</b> (every reason).
          Wire it to a Save-to-Blob and you have the fix-list a data owner can work from.
        </Text>
      </Alert>
    </Stack>
  );
}
